import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Components
import CardMemberAvatar from '@atoms/CardMemberAvatar';
import MailIcon from '@icons/MailIcon';
import FavoriteIcon from '@icons/FavoriteIcon';
import { BodySmall, H4, LabelLarge, LinkRouter } from '@styled';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import { getMemberFullName } from '@general/entities/member';
import { ProductsStrings } from '@general/helpers/SavedSearchesQuestionStrings';
import { transformLocation } from '@general/location';
import { capitalize } from '@general/utility';
import useAuthorized from '@hooks/useAuthorized';
import getFavoriteHook from '@hooks/getFavoriteHook';

const CardOuterStyled = styled.div`
    height: 100%;
    padding: 20px 32px;

    @media ${mediaQueries.lg} {
        display: flex;
        flex-direction: row;
        padding: 40px 32px;
    }
`;

const ImageContainerStyled = styled.div`
    display: flex;
    justify-content: center;

    @media ${mediaQueries.lg} {
        display: block;
    }
`;

const BodyContainerStyled = styled.div`
    flex: 1;
    margin-top: 0.5rem;

    @media ${mediaQueries.lg} {
        margin-left: 1.5rem;
        margin-top: 0;
    }
`;

const ListStyled = styled.ul`
    margin-bottom: 0;
    padding-left: 1rem;
`;

const ActionsContainerStyled = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 0.5rem;

    @media ${mediaQueries.lg} {
        justify-content: flex-start;
        margin-left: 0.5rem;
        margin-top: 0;
    }
`;

const FeaturedServicesCard = ({ isFavorite: initialIsFavorite, savedSearch, member }) => {
    const [memberName, setMemberName] = useState();
    const { isLoggedIn } = useAuthorized();
    const useFavorite = getFavoriteHook({ type: 'member', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();

    const onFavorite = () => {
        if (isFavorite) {
            removeFavorite(member.id);
        } else {
            saveFavorite(member.id);
        }
    };

    useEffect(() => {
        if (!member) {
            return;
        }

        const { firstName, lastName } = member;
        setMemberName(getMemberFullName(firstName, lastName));
    }, [member]);

    return (
        <CardOuterStyled className="card">
            <ImageContainerStyled>
                <CardMemberAvatar
                    firstName={member.firstName}
                    lastName={member.lastName}
                    fullName={memberName}
                    photoURL={member.photoUrl}
                    sizeDesktop={80}
                />
            </ImageContainerStyled>

            <BodyContainerStyled>
                <H4 className="mb-0.5rem">{memberName}</H4>

                {member.memberTitle && <BodySmall className="block text-dark-gray">{member.memberTitle}</BodySmall>}
                {(member.city || member.state || member.country) && (
                    <BodySmall className="block text-dark-gray">
                        {transformLocation({ city: member.city, state: member.state, country: member.country })}
                    </BodySmall>
                )}

                {member.companyName && (
                    <BodySmall className="block text-dark-gray">
                        {member.companyName} {member.businessType.length > 0 ? `(${member.businessType[0]})` : ''}
                    </BodySmall>
                )}

                {savedSearch.businessLocations && (
                    <div className="mt-0.5rem">
                        <LabelLarge>Regions Served:</LabelLarge>
                        <ListStyled>
                            {savedSearch.businessLocations.map((location, index) => (
                                <li key={index}>
                                    <BodySmall>{capitalize(location)}</BodySmall>
                                </li>
                            ))}
                        </ListStyled>
                    </div>
                )}

                <div>
                    {savedSearch.products?.productTypes && (
                        <div className="mt-0.5rem">
                            <LabelLarge>Services Provided:</LabelLarge>
                            <ListStyled>
                                {savedSearch.products?.productTypes.map((productType, index) => (
                                    <li key={index}>
                                        <BodySmall>
                                            {ProductsStrings.productTypes[`${productType.toUpperCase()}`]}
                                        </BodySmall>
                                    </li>
                                ))}
                            </ListStyled>
                        </div>
                    )}
                </div>

                <div className="mt-0.5rem">
                    <BodySmall>
                        <LinkRouter
                            className="font-bold text-azure"
                            to={{
                                pathname: `/member/profile/${member.id}`,
                                state: { graphID: btoa(`01:Member${member.id}`) },
                            }}
                        >
                            View Profile
                        </LinkRouter>
                    </BodySmall>
                </div>
            </BodyContainerStyled>

            <ActionsContainerStyled>
                <MailIcon
                    id={member.id}
                    graphID={member.memberId}
                    companyID={member.companyId}
                    companyName={member.companyName}
                    title={member.memberTitle}
                    fullName={member.fullName}
                    firstName={member.firstName}
                    lastName={member.lastName}
                    designations={member.designations.join(', ')}
                    displayLocation={transformLocation(member)}
                    businessType={member.businessType}
                    photoURL={member.photoURL}
                    status={member.status}
                    isFavorite={isFavorite}
                    isLoggedIn={isLoggedIn}
                />

                <FavoriteIcon
                    id={member.id}
                    type="member"
                    isLoggedIn={isLoggedIn}
                    isFavorite={isFavorite}
                    isLoading={isLoadingFavorite}
                    tooltipLabel="Contact"
                    onClick={onFavorite}
                />
            </ActionsContainerStyled>
        </CardOuterStyled>
    );
};

FeaturedServicesCard.propTypes = {
    isFavorite: PropTypes.bool,
    member: PropTypes.object,
    savedSearch: PropTypes.object,
};

export default FeaturedServicesCard;
